import { apiErrorField } from '@agos/api-client'
import {
  getGetAttendanceQueryKey,
  getGetAttendanceMonthlyQueryKey,
  useDeleteAttendanceId,
  useGetAttendance,
  useGetAttendanceMonthly,
  useGetEmployees,
  usePatchAttendanceId,
  usePostAttendance,
} from '@agos/api-client/hr'
import { useGetHikvisionConfig } from '@agos/api-client/devices'
import { useGetSettingsHourlyRates, usePutSettingsHourlyRates } from '@agos/api-client/platform'
import { T, formatCount, formatDateTime, tt, useAuth,
  roleLabel,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,

  useToast,
} from '@agos/ui'
import { monthColumns } from './month-columns'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Briefcase,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  Pencil,
  Printer,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  canSaveShift,
  deviceRecordCount,
  formatCents,
  formatDuration,
  formatTime,
  isImplausible,
  looksOvernight,
  matchesStaffSearch,
  monthCells,
  monthLabel,
  sheetDate,
  payrollIsComplete,
  payrollRows,
  withoutIdleRows,
  payrollTotals,
  shiftFormOf,
  shiftMonth,
  shiftPayload,
  shiftProblems,
  terminalState,
  todayRows,
  todayTotals,
  typeLabel,
  workedMs,
  type AttendanceRecord,
  type PayrollRow,
  type RateMap,
  type ShiftForm,
  type ShiftProblem,
  type ShiftState,
  type StaffMember,
  type TodayRow,
} from './time-sheet'

/**
 * `/attendance` — who is at work, what a month came to, and what it costs.
 *
 * **FB-101.** The hourly rate is stored per employee id and three of the thirteen people with
 * completed shifts have none, so 229,2 of the 5 851,8 recorded hours are priced at zero euros and
 * legacy's *Výkaz miezd* total says nothing about it. A rate of nought — two developer accounts — is a
 * different thing and reads differently.
 *
 * **FB-102.** A day with no check-out contributes no hours, and **55 of the 542 records have none**,
 * across 14 of the 17 people with any attendance. Legacy's row shows `days` beside `totalMs`, so a
 * month reads as *"22 dní · 148h"* with four of those days worth nothing and no indication which.
 *
 * Both are the same defect: a figure somebody is paid from, quietly missing a component. The hours are
 * split into counted and not counted, every reason is named, and the total says what it excludes.
 *
 * Ported from legacy `pages/attendance.tsx` (1 072 lines).
 */

const POLL_MS = 30_000

type Tab = 'today' | 'month' | 'payroll'

/**
 * §9.146 — the payroll sheet on paper.
 *
 * Same shape as `TripLogPage`, which is this codebase's own precedent: hide everything, show the one
 * region, and drop the controls that are how you got to the number rather than part of the sheet.
 * Portrait rather than landscape — five columns fit, and a wage sheet is filed with other A4.
 */
const PAYROLL_PRINT_STYLES = `
@media print {
  @page { size: A4 portrait; margin: 14mm; }
  body * { visibility: hidden; }
  #payroll-print, #payroll-print * { visibility: visible; }
  /* §9.263 — \`inset: 0\` pinned the sheet's bottom to the viewport, and the container is
   * \`overflow-x-auto\`, which computes \`overflow-y\` to \`auto\` with it. Measured on this data: a
   * 900 px box around 6 369 px of content, \`document.scrollHeight\` stuck at 900, and the whole
   * wage report — 22 rows and 13 employee sheets — printed as **one page**. No \`bottom\`, so the
   * height is the content's; \`overflow: visible\`, so the scroll container cannot clip it. */
  #payroll-print {
    position: absolute; top: 0; left: 0; right: auto; bottom: auto;
    width: 100%; overflow: visible;
  }
  .payroll-screen-only { display: none !important; }
  #payroll-print tr { page-break-inside: avoid; }
  /* §9.184 — a sheet per employee. The last one reverts to auto, or the run ends blank. */
  .payroll-print-only { display: block !important; }
  .payroll-page-break { page-break-after: always; }
  .payroll-page-break:last-child { page-break-after: auto; }
}
/* Off screen entirely: this is a document, not a panel. */
.payroll-print-only { display: none; }
`

export function AttendancePage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user } = useAuth()
  const isMainAdmin = user?.systemRole === 'main_admin'

  const [today] = useState(() => localDate(new Date()))
  const [nowMinutes] = useState(() => new Date().getHours() * 60 + new Date().getMinutes())
  const [tab, setTab] = useState<Tab>('today')
  const [date, setDate] = useState(today)
  const [month, setMonth] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() + 1 }
  })
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('all')
  // §9.261, owner O12 — on by default: the overview is read to see who worked, and a wall of empty
  // strips is what the owner asked to be rid of. The wage sheet below keeps everybody regardless.
  const [hideIdle, setHideIdle] = useState(true)
  const [editing, setEditing] = useState<{
    employeeId: number
    employeeName: string
    date: string
    record: AttendanceRecord | null
    form: ShiftForm
    overnight: boolean
  } | null>(null)
  const [deleting, setDeleting] = useState<AttendanceRecord | null>(null)

  const { data: staffData } = useGetEmployees()
  const staff = useMemo<StaffMember[]>(() => (staffData ?? []), [staffData])

  const { data: dayData, isLoading: loadingDay } = useGetAttendance(
    { date },
    { query: { refetchInterval: tab === 'today' ? POLL_MS : false } },
  )
  const dayRecords = useMemo<AttendanceRecord[]>(() => (dayData ?? []), [dayData])

  const { data: monthData, isLoading: loadingMonth } = useGetAttendanceMonthly(
    { year: month.year, month: month.month },
    { query: { enabled: tab !== 'today' } },
  )
  const monthRecords = useMemo<AttendanceRecord[]>(
    () => ((monthData ?? {}) as { records?: AttendanceRecord[] }).records ?? [],
    [monthData],
  )

  const { data: rateData } = useGetSettingsHourlyRates()
  const rates = (rateData ?? {}) as RateMap
  const { data: terminalData } = useGetHikvisionConfig()
  const terminal = terminalState(terminalData)

  const rows = todayRows(staff, dayRecords, today).filter(
    (row) => (role === 'all' || row.systemRole === role) && matchesStaffSearch(row, search),
  )
  const dayTotals = todayTotals(rows)
  const payroll = payrollRows(monthRecords, staff, rates, today).filter(
    (row) => (role === 'all' || row.role === role) && matchesStaffSearch(row, search),
  )
  const totals = payrollTotals(payroll)
  // Only the monthly overview drops them. `payroll` — and so the wage sheet, its totals and its
  // print — still carries every active employee, because a nought on a wage document is evidence
  // that somebody was not paid rather than noise.
  const monthGrid = withoutIdleRows(payroll)
  const fromDevice = deviceRecordCount(monthRecords)

  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Zmena sa neuložila.'),
      variant: 'destructive',
    })
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetAttendanceQueryKey({ date }) })
    void queryClient.invalidateQueries({
      queryKey: getGetAttendanceMonthlyQueryKey({ year: month.year, month: month.month }),
    })
  }

  const record = usePostAttendance({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        invalidate()
      },
      onError,
    },
  })
  const amend = usePatchAttendanceId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        invalidate()
      },
      onError,
    },
  })
  const remove = useDeleteAttendanceId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })
  const saveRates = usePutSettingsHourlyRates({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries()
      },
      onError,
    },
  })

  const openEdit = (row: TodayRow, forDate: string) =>
    setEditing({
      employeeId: row.id ?? 0,
      employeeName: row.name ?? '',
      date: forDate,
      record: row.record,
      form: shiftFormOf(row.record),
      overnight: looksOvernight(row.record),
    })

  const clockIn = (row: TodayRow) => {
    const now = new Date()
    record.mutate({
      data: {
        employeeId: row.id ?? 0,
        workDate: today,
        checkIn: now.toISOString(),
        type: 'manual',
      },
    })
  }

  const clockOut = (row: TodayRow) => {
    if (row.record?.id === undefined) return
    amend.mutate({ id: row.record.id, data: { checkOut: new Date().toISOString() } })
  }

  const setRate = (employeeId: number, value: string) => {
    // `Record<string, number>`, not `unknown`: the endpoint is declared as employee id to euros per
    // hour now (FB-101), so a value that is not a number no longer type-checks — and the server drops
    // one rather than passing it through, because a pay rate that is a string either crashes a client
    // or silently pays somebody nothing.
    // `RateMap` stays tolerant on the way in — the stored JSON is free-form and a rate may arrive as
    // a string — so the write path coerces rather than casting. Anything that is not a number is
    // dropped, which is what the server does with it too.
    const next: Record<string, number> = {}
    for (const [id, rate] of Object.entries(rates)) {
      const asNumber = Number(rate)
      if (Number.isFinite(asNumber)) next[id] = asNumber
    }
    const parsed = Number(value.trim().replace(',', '.'))
    if (value.trim() === '') delete next[String(employeeId)]
    else if (Number.isFinite(parsed)) next[String(employeeId)] = parsed
    saveRates.mutate({ data: next })
  }

  const roleOptions = useMemo(() => {
    const seen = new Set(staff.filter((member) => member.active !== false).map((member) => member.systemRole ?? ''))
    return [
      { value: 'all', label: tt('Všetky role') },
      // §9.258 — this used the key as its own label, so the filter read *waiter · admin · driver ·
      // main_admin · cook*. `roleLabel` is in `shared` since §9.257 for exactly this. The options
      // stay derived from the staff — a franchise with no drivers is not offered *Šofér* — and are
      // sorted by the name rather than by whichever employee row came back first.
      ...[...seen]
        .filter((value) => value !== '')
        .map((value) => ({ value, label: roleLabel(value) }))
        .sort((left, right) => left.label.localeCompare(right.label, 'sk')),
    ]
  }, [staff])

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Dochádzka')}
        description={tt('Príchody a odchody, mesačný prehľad a hodiny, z ktorých sa platí.')}
        icon={<Clock className="w-5 h-5" />}
      />

      {/* Every record in this data was typed by a person; legacy's icons imply otherwise. */}
      {terminal === 'absent' && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-no-terminal>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>
              Terminál dochádzky nie je nastavený, takže každý príchod a odchod tu niekto zapísal
              ručne.
            </T>
            {fromDevice > 0 && (
              <>
                {' '}
                <T>Zo zariadenia prišlo</T> {formatCount(fromDevice)} <T>záznamov.</T>
              </>
            )}
          </span>
        </p>
      )}

      <TabGroup
        variant="bar"
        tabs={[
          { id: 'today', label: tt('Dnes'), icon: UserCheck },
          { id: 'month', label: tt('Mesačný prehľad'), icon: CalendarDays },
          // The payroll carries wages; legacy restricts the tab to a main administrator and so does
          // this. Nothing on the other two tabs shows a rate.
          ...(isMainAdmin ? [{ id: 'payroll', label: tt('Výkaz miezd'), icon: Briefcase }] : []),
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať meno alebo pozíciu...'),
        }}
        filters={
          <FilterSelect value={role} label={tt('Rola')} onChange={setRole} options={roleOptions} />
        }
      />

      {tab === 'today' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              aria-label={tt('Deň')}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            />
            {date !== today && (
              <button
                type="button"
                onClick={() => setDate(today)}
                aria-label={tt('Späť na dnes')}
                className="px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
              >
                <T>Späť na dnes</T>
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-day-totals>
            <StatCard label={tt('V práci')} value={formatCount(dayTotals.present)} icon={UserCheck} color="emerald" />
            <StatCard label={tt('Odišli')} value={formatCount(dayTotals.finished)} icon={LogOut} color="blue" />
            <StatCard
              label={tt('Neodpichnutý odchod')}
              value={formatCount(dayTotals.open)}
              icon={AlertCircle}
              color={dayTotals.open > 0 ? 'amber' : 'emerald'}
            />
            <StatCard label={tt('Neprišli')} value={formatCount(dayTotals.absent)} icon={X} color="blue" />
          </div>

          {/* FB-102 - the day that will be worth nothing, said on the day. */}
          {dayTotals.open > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-open-shifts>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <span>
                <strong>{dayTotals.open}</strong>{' '}
                <T>
                  zmien nemá zapísaný odchod. Kým sa nedoplní, počítajú sa ako nula hodín - vo výkaze
                  miezd sa objavia ako deň bez hodín.
                </T>
              </span>
            </p>
          )}

          {loadingDay ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            /* §9.321, owner Q7 - *"make it as table, I hate that design there… so use that table
             * component we have for ordering."* `DataTable` is what every other list on the manager
             * is built on, and this tab was a hand-rolled stack of cards with no sorting at all.
             *
             * `rowAttributes` keeps `data-attendance-row`, which `attendance.spec.ts` locates rows by
             * in twelve places - the component's own note says that is the difference between a
             * refactor and a rewrite. */
            <div data-attendance-rows>
              <DataTable
                data={rows}
                rowKey={(row) => row.id ?? 0}
                rowAttributes={(row) => ({ 'data-attendance-row': row.id ?? 0 })}
                emptyMessage={tt('Nikto nezodpovedá filtru.')}
                pageSize={50}
                compact
                columns={[
                  {
                    key: 'name',
                    header: tt('Zamestnanec'),
                    sortable: true,
                    sortValue: (row) => row.name ?? '',
                    render: (row) => (
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{row.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {row.position}
                          {row.record !== null && ` · ${typeLabel(row.record.type)}`}
                        </p>
                      </div>
                    ),
                  },
                  {
                    key: 'state',
                    header: tt('Stav'),
                    sortable: true,
                    sortValue: (row) => row.state,
                    render: (row) => <StateBadge state={row.state} />,
                  },
                  {
                    key: 'in',
                    header: tt('Príchod'),
                    className: 'tabular-nums whitespace-nowrap',
                    sortable: true,
                    sortValue: (row) => row.record?.checkIn ?? null,
                    render: (row) => formatTime(row.record?.checkIn),
                  },
                  {
                    key: 'out',
                    header: tt('Odchod'),
                    className: 'tabular-nums whitespace-nowrap',
                    sortable: true,
                    sortValue: (row) => row.record?.checkOut ?? null,
                    render: (row) => (
                      <span data-shift-times>{formatTime(row.record?.checkOut)}</span>
                    ),
                  },
                  {
                    key: 'hours',
                    header: tt('Hodiny'),
                    headerClassName: 'text-right',
                    className: 'text-right',
                    sortable: true,
                    // The milliseconds, never the formatted string: `formatDuration` writes
                    // `10h 00min`, and `"1h 05min"` sorts *after* `"10h 00min"` as text.
                    sortValue: (row) => workedMs(row.record),
                    render: (row) => (
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <span className="font-bold tabular-nums" data-shift-hours>
                          {formatDuration(workedMs(row.record))}
                        </span>
                        {row.record !== null && isImplausible(row.record) && (
                          <span className="text-[10px] text-amber-500" data-implausible>
                            <T>nezvyčajne dlhá</T>
                          </span>
                        )}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    className: 'text-right',
                    render: (row) => (
                      <div className="flex items-center gap-1 justify-end">
                        {row.state === 'absent' && date === today && (
                          <button
                            type="button"
                            onClick={() => clockIn(row)}
                            aria-label={`${tt('Zapísať príchod')} ${row.name ?? ''}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-500 text-xs font-semibold hover:bg-emerald-500/10"
                          >
                            <LogIn className="w-3.5 h-3.5" />
                            <T>Príchod</T>
                          </button>
                        )}
                        {row.state === 'working' && (
                          <button
                            type="button"
                            onClick={() => clockOut(row)}
                            aria-label={`${tt('Zapísať odchod')} ${row.name ?? ''}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            <T>Odchod</T>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openEdit(row, date)}
                          aria-label={`${tt('Upraviť dochádzku')} ${row.name ?? ''}`}
                          className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {row.record !== null && (
                          <button
                            type="button"
                            onClick={() => setDeleting(row.record)}
                            aria-label={`${tt('Zmazať dochádzku')} ${row.name ?? ''}`}
                            className="p-1.5 rounded-lg border border-border text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </>
      )}

      {tab !== 'today' && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMonth(shiftMonth(month.year, month.month, -1))}
            aria-label={tt('Predchádzajúci mesiac')}
            className="p-2 rounded-lg border border-border hover:bg-muted/50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-bold w-40 text-center" data-month-label>
            {monthLabel(month.year, month.month)}
          </span>
          <button
            type="button"
            onClick={() => setMonth(shiftMonth(month.year, month.month, 1))}
            aria-label={tt('Nasledujúci mesiac')}
            className="p-2 rounded-lg border border-border hover:bg-muted/50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {/* §9.261, owner O12 - the overview only. Off it goes back to what `payrollRows` builds,
            * which is everybody active; the count says how many that is rather than leaving the
            * difference between the two states to be discovered. */}
          {tab === 'month' && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideIdle}
                onChange={(event) => setHideIdle(event.target.checked)}
                data-hide-idle
                className="w-4 h-4 rounded border-border"
              />
              <T>Skryť bez dochádzky</T>
              {monthGrid.hidden > 0 && <span data-idle-count>({formatCount(monthGrid.hidden)})</span>}
            </label>
          )}
        </div>
      )}

      {tab === 'month' &&
        (loadingMonth ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-3" data-month-grid>
            {(hideIdle ? monthGrid.rows : payroll).map((row) => (
              <MonthStrip
                key={row.employeeId}
                row={row}
                year={month.year}
                month={month.month}
                today={today}
                onOpenDay={(entry) =>
                  setEditing({
                    employeeId: row.employeeId,
                    employeeName: row.name,
                    date: entry.workDate ?? today,
                    record: entry,
                    form: shiftFormOf(entry),
                    overnight: looksOvernight(entry),
                  })
                }
              />
            ))}
            {(hideIdle ? monthGrid.rows : payroll).length === 0 && (
              <p className="text-xs text-muted-foreground py-8 text-center" data-month-empty>
                {hideIdle && payroll.length > 0 ? (
                  <T>V tomto mesiaci nikto nepracoval.</T>
                ) : (
                  <T>Nikto nezodpovedá filtru.</T>
                )}
              </p>
            )}
          </div>
        ))}

      {tab === 'payroll' && isMainAdmin && (
        <>
          <style>{PAYROLL_PRINT_STYLES}</style>
          <div className="flex justify-end payroll-screen-only">
            <button
              type="button"
              onClick={() => window.print()}
              data-print-payroll
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <Printer className="w-4 h-4" /> <T>Tlačiť výkaz</T>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 payroll-screen-only" data-payroll-totals>
            <StatCard
              label={tt('Započítané hodiny')}
              value={formatDuration(totals.countedMs)}
              icon={Clock}
              color="blue"
            />
            <StatCard
              label={tt('Na výplatu')}
              value={formatCents(totals.earnedCents)}
              icon={Briefcase}
              color="violet"
            />
            <StatCard
              label={tt('Bez hodinovej sadzby')}
              value={formatCount(totals.withoutRate)}
              sub={totals.msWithoutRate > 0 ? formatDuration(totals.msWithoutRate) : undefined}
              icon={AlertCircle}
              color={totals.withoutRate > 0 ? 'red' : 'emerald'}
            />
            <StatCard
              label={tt('Dni bez hodín')}
              value={formatCount(totals.openDays + totals.impossibleDays)}
              icon={AlertCircle}
              color={totals.openDays + totals.impossibleDays > 0 ? 'amber' : 'emerald'}
            />
          </div>

          {/* FB-101 and FB-102 - what the figure above does not include. */}
          {!payrollIsComplete(totals) && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1 payroll-screen-only" data-payroll-incomplete>
              <p className="text-xs font-bold text-amber-500">
                <T>Suma na výplatu nie je celá.</T>
              </p>
              {totals.withoutRate > 0 && (
                <p className="text-xs text-amber-500" data-payroll-no-rate>
                  <strong>{totals.withoutRate}</strong>{' '}
                  <T>zamestnancov nemá nastavenú hodinovú sadzbu -</T>{' '}
                  <strong>{formatDuration(totals.msWithoutRate)}</strong>{' '}
                  <T>odpracovaných hodín sa počíta ako nula eur.</T>
                </p>
              )}
              {totals.openDays > 0 && (
                <p className="text-xs text-amber-500" data-payroll-open>
                  <strong>{totals.openDays}</strong>{' '}
                  <T>dní nemá zapísaný odchod, takže neprispievajú žiadnymi hodinami.</T>
                </p>
              )}
              {totals.impossibleDays > 0 && (
                <p className="text-xs text-red-500" data-payroll-impossible>
                  <strong>{totals.impossibleDays}</strong>{' '}
                  <T>dní má odchod pred príchodom - treba ich opraviť.</T>
                </p>
              )}
            </div>
          )}

          {loadingMonth ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <div className="overflow-x-auto" data-payroll id="payroll-print">
              {/* Only on paper: a filed sheet has to say which month it covers and when it was
                * taken, or somebody has to recompute it to trust it. */}
              <div className="hidden print:block mb-4 text-black">
                <h1 className="text-lg font-bold">
                  <T>Výkaz miezd</T> - {monthLabel(month.year, month.month)}
                </h1>
                <p className="text-[11px]">
                  <T>Vygenerované</T>: {formatDateTime(new Date().toISOString())}
                </p>
              </div>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2">{tt('Zamestnanec')}</th>
                    <th className="text-right py-2">{tt('Dni')}</th>
                    <th className="text-right py-2">{tt('Hodiny')}</th>
                    <th className="text-right py-2">{tt('Sadzba')}</th>
                    <th className="text-right py-2">{tt('Suma')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.map((row) => (
                    <tr
                      key={row.employeeId}
                      className="border-t border-border"
                      data-payroll-row={row.employeeId}
                    >
                      <td className="py-2">
                        <p className="font-semibold">{row.name}</p>
                        <p className="text-[11px] text-muted-foreground">{row.position}</p>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {row.paidDays}
                        {row.days !== row.paidDays && (
                          <span className="text-amber-500" data-row-unpaid-days>
                            {' '}
                            / {row.days}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right tabular-nums">{formatDuration(row.countedMs)}</td>
                      <td className="py-2 text-right">
                        <input
                          value={row.rateCents === null ? '' : String(row.rateCents / 100)}
                          onChange={(event) => setRate(row.employeeId, event.target.value)}
                          aria-label={`${tt('Hodinová sadzba')} ${row.name}`}
                          placeholder={tt('nenastavená')}
                          className={cn(
                            'w-20 bg-background border rounded-lg px-2 py-1 text-xs text-right tabular-nums',
                            row.rateCents === null && row.countedMs > 0
                              ? 'border-red-500/50'
                              : 'border-border',
                          )}
                        />
                      </td>
                      <td className="py-2 text-right tabular-nums font-bold">
                        {row.rateCents === null ? (
                          <span className="text-red-500 font-normal" data-row-no-rate>
                            <T>bez sadzby</T>
                          </span>
                        ) : row.unpaidByDesign ? (
                          <span className="text-muted-foreground font-normal" data-row-unpaid>
                            <T>neplatené</T>
                          </span>
                        ) : (
                          formatCents(row.earnedCents)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* §9.184 - the summary above is what AGOS printed and all it printed. Legacy follows
                  it with a sheet per person, and that is what makes the document signable: which
                  days those hours came from, and a line to sign under them.

                  `records` has been on `PayrollRow` all along; the summary needs only the totals, so
                  nothing ever read it. Print-only, so the screen is unchanged. */}
              <div className="payroll-print-only">
                <div className="payroll-page-break" />
                {payroll
                  // A blank sheet with a signature line under it is a form somebody might sign.
                  .filter((row) => row.records.length > 0)
                  .map((row) => (
                    <section
                      key={row.employeeId}
                      className="payroll-page-break"
                      data-payroll-sheet={row.employeeId}
                    >
                      <h2 className="text-base font-bold border-b-2 border-foreground pb-1 mb-1.5">
                        {row.name}
                      </h2>
                      <div className="flex flex-wrap gap-4 text-[11px] mb-1">
                        <span>
                          <T>Pozícia</T>: <b>{row.position}</b>
                        </span>
                        <span>
                          <T>Dni</T>: <b>{row.paidDays}</b>
                        </span>
                        <span>
                          <T>Hodiny</T>: <b>{formatDuration(row.countedMs)}</b>
                        </span>
                        {row.rateCents !== null && (
                          <span>
                            <T>Sadzba</T>: <b>{formatCents(row.rateCents)}/h</b>
                          </span>
                        )}
                        {row.earnedCents > 0 && (
                          <span>
                            <T>Mzda</T>: <b>{formatCents(row.earnedCents)}</b>
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mb-2">
                        {monthLabel(month.year, month.month)} · {formatDateTime(new Date().toISOString())}
                      </p>
                      <table className="w-full text-[10px]">
                        <thead className="uppercase tracking-wider">
                          <tr>
                            <th className="text-left py-1">{tt('Dátum')}</th>
                            <th className="text-center py-1">{tt('Príchod')}</th>
                            <th className="text-center py-1">{tt('Odchod')}</th>
                            <th className="text-center py-1">{tt('Hodiny')}</th>
                            <th className="text-left py-1">{tt('Poznámka')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.records.map((entry, index) => (
                            <tr key={entry.id ?? index} className="border-t border-border">
                              <td className="py-1">{sheetDate(entry.workDate)}</td>
                              <td className="py-1 text-center tabular-nums">
                                {formatTime(entry.checkIn)}
                              </td>
                              <td className="py-1 text-center tabular-nums">
                                {formatTime(entry.checkOut)}
                              </td>
                              <td className="py-1 text-center tabular-nums">
                                {formatDuration(workedMs(entry))}
                              </td>
                              <td className="py-1">{entry.notes ?? ''}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-bold border-t-2 border-foreground">
                            <td className="py-1">
                              <T>SPOLU</T>
                            </td>
                            <td />
                            <td />
                            <td className="py-1 text-center tabular-nums">
                              {formatDuration(row.countedMs)}
                            </td>
                            <td className="py-1">
                              {row.earnedCents > 0 ? `${tt('Mzda')}: ${formatCents(row.earnedCents)}` : ''}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                      {/* Legacy's, and the reason this is printed rather than screenshotted. */}
                      <div className="flex justify-between text-[10px] mt-10">
                        <span>{tt('Podpis zamestnanca')}: ____________________</span>
                        <span>{tt('Podpis vedúceho')}: ____________________</span>
                      </div>
                    </section>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {editing !== null && (
        <ShiftDialog
          state={editing}
          nowMinutes={editing.date === today ? nowMinutes : null}
          saving={record.isPending || amend.isPending}
          onChange={(form) => setEditing({ ...editing, form })}
          onOvernight={(overnight) => setEditing({ ...editing, overnight })}
          onClose={() => setEditing(null)}
          onSave={() => {
            const payload = shiftPayload(
              editing.date,
              editing.employeeId,
              editing.form,
              editing.overnight,
            )
            if (editing.record?.id === undefined) record.mutate({ data: payload })
            else amend.mutate({ id: editing.record.id, data: payload })
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ id: deleting.id })}
        title={tt('Zmazať tento záznam dochádzky?')}
        description={tt(
          'Deň zmizne z prehľadu aj z výkazu miezd. Ak sú časy len nesprávne, opravte ich namiesto zmazania.',
        )}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

// ─── the month strip ─────────────────────────────────────────────────────────

function MonthStrip({
  row,
  year,
  month,
  today,
  onOpenDay,
}: {
  row: PayrollRow
  year: number
  month: number
  today: string
  onOpenDay: (entry: AttendanceRecord) => void
}) {
  const cells = monthCells(year, month, row.records, today)

  // §9.213 — the columns live in `month-columns.ts` with their own tests: what each one *sorts by*
  // is the interesting part and is exactly what a browser test cannot see.
  const columns = monthColumns()

  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-month-strip={row.employeeId}>
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="text-sm font-semibold flex-1 min-w-0 truncate">{row.name}</p>
        <span className="text-xs text-muted-foreground">
          {row.paidDays} <T>dní</T> · {formatDuration(row.countedMs)}
        </span>
        {/* FB-102 - the days that are recorded and worth nothing, per person. */}
        {row.openDays + row.impossibleDays > 0 && (
          <span className="text-xs text-amber-500" data-strip-unpaid>
            {row.openDays + row.impossibleDays} <T>dní bez hodín</T>
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {cells.map((cell) => (
          <span
            key={cell.date}
            title={`${cell.date} · ${formatDuration(cell.ms)}`}
            data-month-cell={cell.state}
            className={cn(
              'w-7 h-7 rounded text-[10px] flex items-center justify-center tabular-nums',
              cell.state === 'finished' && 'bg-emerald-500/15 text-emerald-500',
              cell.state === 'working' && 'bg-blue-500/15 text-blue-500',
              (cell.state === 'open' || cell.state === 'impossible') &&
                'bg-amber-500/20 text-amber-500 font-bold',
              cell.state === 'absent' && 'bg-muted/40 text-muted-foreground',
            )}
          >
            {cell.day}
          </span>
        ))}
      </div>

      {/* §9.213 - the table legacy has, sortable and clickable. The strip above it stays because it
        * is the only view of a day nobody worked: a table of records cannot show an absence. */}
      <DataTable
        columns={columns}
        data={row.records}
        rowKey={(entry, index) => entry.id ?? index}
        rowAttributes={(entry) => ({ 'data-month-row': entry.workDate ?? '' })}
        onRowClick={onOpenDay}
        pageSize={25}
        compact
        emptyMessage={tt('V tomto mesiaci nemá žiadny záznam.')}
      />
    </div>
  )
}

// ─── the correction ──────────────────────────────────────────────────────────

function ShiftDialog({
  state,
  nowMinutes,
  saving,
  onChange,
  onOvernight,
  onClose,
  onSave,
}: {
  state: {
    employeeName: string
    date: string
    record: AttendanceRecord | null
    form: ShiftForm
    overnight: boolean
  }
  nowMinutes: number | null
  saving: boolean
  onChange: (form: ShiftForm) => void
  onOvernight: (overnight: boolean) => void
  onClose: () => void
  onSave: () => void
}) {
  const problems = shiftProblems(state.form, state.overnight, nowMinutes)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${tt('Dochádzka')} ${state.employeeName}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-shift-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h3 className="font-bold text-sm truncate">{state.employeeName}</h3>
            <p className="text-[11px] text-muted-foreground">{state.date}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={tt('Príchod')}>
              <input
                type="time"
                value={state.form.checkIn}
                onChange={(event) => onChange({ ...state.form, checkIn: event.target.value })}
                aria-label={tt('Príchod')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Odchod')}>
              <input
                type="time"
                value={state.form.checkOut}
                onChange={(event) => onChange({ ...state.form, checkOut: event.target.value })}
                aria-label={tt('Odchod')}
                className={INPUT}
              />
            </Field>
          </div>

          {/* Legacy builds both stamps from the same work date, so it cannot express this at all. */}
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={state.overnight}
              onChange={(event) => onOvernight(event.target.checked)}
              aria-label={tt('Odchod na druhý deň')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Odchod je na druhý deň - nočná zmena</T>
          </label>

          <Field label={tt('Poznámka')}>
            <input
              value={state.form.notes}
              onChange={(event) => onChange({ ...state.form, notes: event.target.value })}
              aria-label={tt('Poznámka')}
              className={INPUT}
            />
          </Field>

          {problems.length > 0 && (
            <div className="space-y-1" data-shift-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'implausible' ? 'text-amber-500' : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ShiftProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !canSaveShift(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ShiftProblemText({ problem }: { problem: ShiftProblem }) {
  if (problem.kind === 'no-times') return <T>Zadajte aspoň príchod.</T>
  if (problem.kind === 'out-without-in') return <T>Odchod bez príchodu nedáva hodiny.</T>
  if (problem.kind === 'out-before-in') {
    return <T>Odchod je pred príchodom. Ak išlo o nočnú zmenu, zaškrtnite „na druhý deň“.</T>
  }
  if (problem.kind === 'in-the-future') return <T>Ten čas ešte nenastal.</T>
  return (
    <span>
      <T>Zmena dlhá</T> {problem.hours} <T>hodín - je to naozaj tak?</T>
    </span>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

const STATE_LABELS: Record<ShiftState, string> = {
  working: tt('v práci'),
  finished: tt('odišiel'),
  open: tt('chýba odchod'),
  impossible: tt('nesprávne časy'),
  absent: tt('neprišiel'),
}

const STATE_COLORS: Record<ShiftState, 'emerald' | 'blue' | 'amber' | 'red' | 'zinc'> = {
  working: 'emerald',
  finished: 'blue',
  open: 'amber',
  impossible: 'red',
  absent: 'zinc',
}

function StateBadge({ state }: { state: ShiftState }) {
  return (
    <span data-shift-state={state}>
      <StatusBadge label={STATE_LABELS[state]} color={STATE_COLORS[state]} />
    </span>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}

/** Today as the restaurant's calendar has it, not as UTC does. */
function localDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
